import { CircularProgress, IconButton, InputAdornment, TextField, TextFieldProps } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import SearchIcon from '@material-ui/icons/Search';
import React, { forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react';

interface IOwnProps {
  onCancel?: () => void;
  onValueChange?: (v: string) => void;
  defaultValue?: string;
  isSearching?: boolean;
  onValueSubmit?: (v: string) => void;
}

export type ISearchTextField = IOwnProps & TextFieldProps;

export interface ISearchTextFieldImperativeHandle {
  clear: () => void;
  getValue: () => string;
  focus: () => void;
}

const SearchTextField = (props: ISearchTextField, ref: Ref<unknown>): JSX.Element => {
  const {
    defaultValue = '',
    isSearching: isSearchingProps = false,
    onValueSubmit,
    onValueChange,
    onCancel,
    InputProps,
    onChange,
    value: _,
    ...restProps
  } = props;

  const [thisText, setThisText] = useState<string>(defaultValue);
  const [isSearching, setIsSearching] = useState<boolean>(isSearchingProps);
  const inputRef = useRef<HTMLInputElement>();

  const handleCancelClick = () => {
    setThisText('');
    onCancel?.();
  };

  useImperativeHandle<unknown, ISearchTextFieldImperativeHandle>(
    ref,
    () => ({
      clear: () => {
        setThisText('');
      },
      getValue: () => thisText,
      focus: () => inputRef.current?.focus(),
    }),
    [thisText],
  );

  useEffect(() => {
    setIsSearching(isSearchingProps);
  }, [isSearchingProps]);

  useEffect(() => {
    setThisText(defaultValue);
  }, [defaultValue]);

  return (
    <TextField
      inputRef={inputRef}
      value={thisText}
      onChange={e => {
        setThisText(e.target.value);
        onValueChange?.(e.target.value);
        onChange?.(e);
      }}
      onKeyPress={e => {
        if (e.key === 'Enter') {
          onValueSubmit?.(thisText);
          inputRef.current?.blur();
        }
      }}
      InputProps={{
        style: {
          paddingTop: 1,
          paddingBottom: 1,
        },
        endAdornment: (
          <InputAdornment position="end">
            {thisText && (
              <IconButton size="small" onClick={handleCancelClick}>
                <CancelIcon htmlColor="#999" fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              disabled={isSearchingProps}
              onClick={() => {
                onValueSubmit?.(thisText);
              }}
            >
              {isSearching ? <CircularProgress size={18} /> : <SearchIcon />}
            </IconButton>
          </InputAdornment>
        ),
        ...InputProps,
      }}
      {...restProps}
    />
  );
};

export default forwardRef(SearchTextField);
